<template>
  <!-- TODO: migrate -->
  <transition
    v-bind="transitionProps"
    @after-enter="transitioning = false" @before-leave="transitioning = true"
  >
  <component
    v-bind="layoutProps"
    :is="layoutComponent"
    class="layout-component"
    :class="{'layout-transitioning': transitioning}"
  />
   </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { getRouteInheritedMeta } from '@/utils/helpers'

import DefaultLayout from '@/layouts/default.vue'
import FocusLayout from '@/layouts/focus.vue'
import FocusDetailLayout from '@/layouts/focus-detail.vue'
import FullscreenLayout from '@/layouts/fullscreen.vue'

// Reactive state
const layout = ref(null)
const transitionName = ref(null)
const transitioning = ref(false)

// Composables
const route = useRoute()

// Computed properties
const layoutComponent = computed(() => {
  if (!layout.value) return null
  // Causes unexpected behavior with the transition
  // return defineAsyncComponent(() => import(`@/layouts/${layout.value}.vue`))
  switch (layout.value) {
    case 'default':
      return DefaultLayout
    case 'focus':
      return FocusLayout
    case 'focus-detail':
      return FocusDetailLayout
    case 'fullscreen':
      return FullscreenLayout
  }
})

const layoutProps = computed(() => {
  return getRouteInheritedMeta(route).layoutProps
})

const transitionProps = computed(() => {
  if (transitionName.value) {
    return {
      name: transitionName.value,
      mode: 'out-in',
    }
  }

  // don't delay route switch with out-in
  return {}
})

// Methods
function getRouteLayout(route) {
  const meta = getRouteInheritedMeta(route)

  if (typeof meta.layout === 'function') {
    // In Composition API we don't have 'this', so we'd need to provide
    // necessary context if the layout function needs it
    return meta.layout()
  }

  return meta.layout ?? 'default'
}

function getLayoutTransition(to) {
  let inheritedToTransition = null

  // NOTE do we not want to use the from route's layout transition
  // it probably makes more sense to use it as a leave transition rather than enter

  for (const r of to.matched) {
    if (r.meta.layoutTransition) inheritedToTransition = r.meta.layoutTransition
  }

  return inheritedToTransition ?? 't-fade'
}

// Watchers
watch(() => route, (to, from) => {
  const newLayout = getRouteLayout(to)
  if (layout.value !== newLayout) {
    layout.value = newLayout
  }

  // no layout transition on first route
  if (from) {
    transitionName.value = getLayoutTransition(to, from)
  }
}, { deep: true, immediate: true })

// Lifecycle hooks
onMounted(() => {
  // Initialize on first load
  const newLayout = getRouteLayout(route)
  if (layout.value !== newLayout) {
    layout.value = newLayout
  }
})

// Component naming for debugging
defineOptions({
  name: 'Layout',
})
</script>

<style lang="scss" scoped>
.layout-component {
  top: 0;
  left: 0;
  width: 100%;

  // &.layout-transitioning {
  //   overflow: hidden;
  //   height: 100vh;

  //   &:deep(.route-page) {
  //     overflow: hidden;
  //     height: 100vh;
  //   }
  // }
}
</style>
